import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft, Calendar, DollarSign, Edit, Loader2, FileText,
  AlertCircle, Package, Plus, Trash2, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { DateCell } from "@/components/DateCell";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface VqLine {
  id: string;
  lineNumber: number;
  lineKind: string;
  description: string;
  vendorSku: string | null;
  quantity: string;
  uom: string;
  unitCost: string;
  extendedAmount: string | null;
  leadTimeDays: number | null;
  notes: string | null;
  disposition: string;
  resolvedProductId: string | null;
  product?: { id: string; name: string; sku: string | null };
}

interface Vq {
  id: string;
  quoteNumber: string;
  vendorId: string;
  vendor?: { id: string; name: string };
  vendorName?: string;
  vendorQuoteNumber: string | null;
  quoteDate: string;
  validUntil: string | null;
  leadTimeDays: number | null;
  paymentTerms: string | null;
  currencyCode: string | null;
  intendedPurpose: string | null;
  status: string;
  priority: string;
  subtotal: string | null;
  total: string | null;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  lines?: VqLine[];
}

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "RECEIVED", label: "Received" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "PARTIALLY_CONVERTED", label: "Partially Converted" },
  { value: "FULLY_CONVERTED", label: "Fully Converted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "SUPERSEDED", label: "Superseded" },
];

const PRIORITIES = [
  { value: "URGENT", label: "Urgent" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

const LINE_KINDS = [
  { value: "ITEM", label: "Item" },
  { value: "FREIGHT", label: "Freight" },
  { value: "CHARGE", label: "Charge" },
  { value: "NOTE", label: "Note" },
];

function formatStatusLabel(s: string) {
  return s.replace(/_/g, " ").split(" ").map(w => w[0] + w.slice(1).toLowerCase()).join(" ");
}

function statusDotColor(status: string) {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "DRAFT": return "bg-gray-400";
    case "RECEIVED": return "bg-blue-500";
    case "UNDER_REVIEW": return "bg-yellow-500";
    case "ACCEPTED": return "bg-green-500";
    case "PARTIALLY_CONVERTED": return "bg-indigo-400";
    case "FULLY_CONVERTED": return "bg-emerald-500";
    case "REJECTED": return "bg-red-500";
    case "EXPIRED": return "bg-orange-500";
    case "CANCELLED": return "bg-gray-500";
    case "SUPERSEDED": return "bg-slate-500";
    default: return "bg-gray-400";
  }
}

export default function VendorQuoteDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/vendor-quotes/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = params?.id;

  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("DRAFT");
  const [editPriority, setEditPriority] = useState("NORMAL");
  const [editValidUntil, setEditValidUntil] = useState("");
  const [editLeadTime, setEditLeadTime] = useState("");
  const [editPaymentTerms, setEditPaymentTerms] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");

  const [addLineOpen, setAddLineOpen] = useState(false);
  const [lineKind, setLineKind] = useState("ITEM");
  const [lineDesc, setLineDesc] = useState("");
  const [lineSku, setLineSku] = useState("");
  const [lineUom, setLineUom] = useState("each");
  const [lineQty, setLineQty] = useState("1");
  const [lineCost, setLineCost] = useState("0.00");
  const [lineLeadTime, setLineLeadTime] = useState("");

  const { data: vq, isLoading, error } = useQuery<Vq>({
    queryKey: ["/api/vendor-quotes", id],
    queryFn: () => api.get<Vq>(`/vendor-quotes/${id}`, sessionId),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.patch<Vq>(`/vendor-quotes/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["vendor-quotes"] });
      toast({ title: "Vendor quote updated" });
      setEditOpen(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addLineMutation = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(lineQty || "0");
      const cost = parseFloat(lineCost || "0");
      return api.post(`/vendor-quotes/${id}/lines`, {
        lineKind,
        description: lineDesc,
        vendorSku: lineSku || null,
        uom: lineUom,
        quantity: String(qty),
        unitCost: String(cost),
        extendedAmount: String((qty * cost).toFixed(2)),
        leadTimeDays: lineLeadTime ? parseInt(lineLeadTime) : null,
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-quotes", id] });
      toast({ title: "Line added" });
      setAddLineOpen(false);
      setLineKind("ITEM");
      setLineDesc("");
      setLineSku("");
      setLineUom("each");
      setLineQty("1");
      setLineCost("0.00");
      setLineLeadTime("");
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteLineMutation = useMutation({
    mutationFn: (lineId: string) =>
      api.delete(`/vendor-quotes/${id}/lines/${lineId}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-quotes", id] });
      toast({ title: "Line deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (vq) {
      setEditStatus((vq.status || "DRAFT").toUpperCase());
      setEditPriority(vq.priority || "NORMAL");
      setEditValidUntil(vq.validUntil ? vq.validUntil.slice(0, 10) : "");
      setEditLeadTime(vq.leadTimeDays != null ? String(vq.leadTimeDays) : "");
      setEditPaymentTerms(vq.paymentTerms || "");
      setEditNotes(vq.notes || "");
      setEditInternalNotes(vq.internalNotes || "");
    }
    setEditOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      status: editStatus,
      priority: editPriority,
      validUntil: editValidUntil || null,
      leadTimeDays: editLeadTime ? parseInt(editLeadTime) : null,
      paymentTerms: editPaymentTerms || null,
      notes: editNotes || null,
      internalNotes: editInternalNotes || null,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !vq) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Vendor quote not found</p>
        <Button variant="outline" onClick={() => setLocation("/vendor-quotes")}>
          Back to Vendor Quotes
        </Button>
      </div>
    );
  }

  const lines = vq.lines || [];
  const total = Number(vq.total || 0);
  const subtotal = Number(vq.subtotal || 0);
  const statusUpper = (vq.status || "DRAFT").toUpperCase();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/vendor-quotes")}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-back-to-vendor-quotes"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-quote-number">
                {vq.quoteNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {vq.vendor && vq.vendorId && (
                <button
                  onClick={() => setLocation(`/vendors/${vq.vendorId}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-vendor"
                >
                  {vq.vendor.name}
                </button>
              )}
              {vq.vendor && <span className="text-muted-foreground font-normal">/</span>}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span data-testid="text-quote-status">{formatStatusLabel(statusUpper)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen} data-testid="button-edit-quote">
              <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </div>

        {/* Mini-cards strip */}
        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto" data-testid="mini-cards-strip">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor(statusUpper))} />
              <span className="text-sm font-medium">{formatStatusLabel(statusUpper)}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Total</span>
              <p className="text-sm font-medium" data-testid="text-total">
                ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Lines</span>
              <p className="text-sm font-medium" data-testid="text-line-count">{lines.length}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Quote Date</span>
              <p className="text-sm font-medium" data-testid="text-quote-date">
                <DateCell value={vq.quoteDate} fallback="—" />
              </p>
            </div>
          </div>
          {vq.validUntil && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground">Valid Until</span>
                  <p className="text-sm font-medium" data-testid="text-valid-until">
                    <DateCell value={vq.validUntil} fallback="—" />
                  </p>
                </div>
              </div>
            </>
          )}
          {vq.leadTimeDays != null && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <div>
                  <span className="text-xs text-muted-foreground">Lead Time</span>
                  <p className="text-sm font-medium">{vq.leadTimeDays} days</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Vendor + Quote Details cards */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />
              Vendor
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Vendor</span>
              <span className="text-sm font-medium">{vq.vendor?.name || vq.vendorName || "—"}</span>
            </div>
            {vq.vendorQuoteNumber && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Vendor Quote #</span>
                <span className="text-sm font-medium">{vq.vendorQuoteNumber}</span>
              </div>
            )}
            {vq.paymentTerms && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Payment Terms</span>
                <span className="text-sm font-medium">{vq.paymentTerms}</span>
              </div>
            )}
            {vq.currencyCode && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Currency</span>
                <span className="text-sm font-medium">{vq.currencyCode}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Priority</span>
              <span className="text-sm font-medium">{vq.priority}</span>
            </div>
          </div>
        </div>

        <div className="border-l-[3px] border-violet-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-violet-500" />
              Quote Details
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {vq.intendedPurpose && (
              <div>
                <span className="text-xs text-muted-foreground">Intended Purpose</span>
                <p className="text-sm whitespace-pre-wrap">{vq.intendedPurpose}</p>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Subtotal</span>
              <span className="text-sm font-medium">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="text-sm font-medium">${total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Created</span>
              <span className="text-sm font-medium"><DateCell value={vq.createdAt} /></span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {(vq.notes || vq.internalNotes) && (
        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {vq.notes && (
              <div>
                <span className="text-xs text-muted-foreground">External Notes</span>
                <p className="text-sm whitespace-pre-wrap">{vq.notes}</p>
              </div>
            )}
            {vq.internalNotes && (
              <div>
                <span className="text-xs text-muted-foreground">Internal Notes</span>
                <p className="text-sm whitespace-pre-wrap">{vq.internalNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Line Items */}
      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-500" />
            Line Items
          </h2>
          <Button size="sm" variant="outline" onClick={() => setAddLineOpen(true)} data-testid="button-add-line">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Line
          </Button>
        </div>
        <div className="px-4 pb-4">
          {lines.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Extended</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id} data-testid={`row-vq-line-${line.lineNumber}`}>
                      <TableCell className="font-medium">{line.lineNumber}</TableCell>
                      <TableCell className="text-xs">{line.lineKind}</TableCell>
                      <TableCell className="text-xs">{line.vendorSku || line.product?.sku || "—"}</TableCell>
                      <TableCell>{line.product?.name || line.description}</TableCell>
                      <TableCell className="text-xs">{line.uom}</TableCell>
                      <TableCell className="text-right">{Number(line.quantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">${Number(line.unitCost || 0).toFixed(4)}</TableCell>
                      <TableCell className="text-right font-medium">
                        ${Number(line.extendedAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {line.leadTimeDays != null ? `${line.leadTimeDays}d` : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteLineMutation.mutate(line.id)}
                          disabled={deleteLineMutation.isPending}
                          data-testid={`button-delete-line-${line.lineNumber}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              <Package className="h-10 w-10 mb-2 opacity-50" />
              <p>No line items</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Vendor Quote</DialogTitle>
            <DialogDescription>Update quote details, status and notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Status *</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="select-edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={editPriority} onValueChange={setEditPriority}>
                  <SelectTrigger data-testid="select-edit-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Valid Until</label>
                <Input type="date" value={editValidUntil} onChange={(e) => setEditValidUntil(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Lead Time (days)</label>
                <Input
                  type="number"
                  value={editLeadTime}
                  onChange={(e) => setEditLeadTime(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Payment Terms</label>
              <Input
                value={editPaymentTerms}
                onChange={(e) => setEditPaymentTerms(e.target.value)}
                placeholder="NET30"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="External notes..."
                className="min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Internal Notes</label>
              <Textarea
                placeholder="Internal notes..."
                className="min-h-[80px]"
                value={editInternalNotes}
                onChange={(e) => setEditInternalNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-quote">
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Line Dialog */}
      <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>Add a vendor quote line.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Kind</label>
                <Select value={lineKind} onValueChange={setLineKind}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LINE_KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Vendor SKU</label>
                <Input value={lineSku} onChange={(e) => setLineSku(e.target.value)} placeholder="ABC-123" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description *</label>
              <Input value={lineDesc} onChange={(e) => setLineDesc(e.target.value)} placeholder="Line description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Quantity *</label>
                <Input type="number" step="0.001" value={lineQty} onChange={(e) => setLineQty(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">UOM</label>
                <Input value={lineUom} onChange={(e) => setLineUom(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Unit Cost *</label>
                <Input type="number" step="0.0001" value={lineCost} onChange={(e) => setLineCost(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lead Time (days)</label>
              <Input
                type="number"
                value={lineLeadTime}
                onChange={(e) => setLineLeadTime(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineOpen(false)}>Cancel</Button>
            <Button
              onClick={() => addLineMutation.mutate()}
              disabled={addLineMutation.isPending || !lineDesc.trim()}
              data-testid="button-save-line"
            >
              {addLineMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}